import { useEffect, useState } from 'react';
import omit from 'lodash/omit';

import { Alert, Button } from '@/shared/ui';
import { useAppDispatch } from '@/shared/hooks/store';
import { IProvider } from '@/shared/types/interfaces/provider';
import { IPharmacy } from '@/shared/types/interfaces/pharmacy';
import { IMedication } from '@/shared/types/interfaces/medication';
import { CustomersController } from '@/shared/endpoints/customers';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';

import { Provider } from './provider';
import { Pharmacy } from './pharmacy';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { toast } from 'react-toastify';

interface IProps {
    medication: IMedication;
    onCreateMedication?: (result?: ITransferPrescriptions | null) => void;
    transferPrescription?: ITransferPrescriptions;
    zipCode: string;
}

const PrescriptionMedicationForm = ({ medication, onCreateMedication, transferPrescription, zipCode }: IProps) => {
    const dispatch = useAppDispatch();
    const [selectedPharmacy, setSelectedPharmacy] = useState<IPharmacy | null>(transferPrescription?.fromPharmacy || null);
    const [selectedProvider, setSelectedProvider] = useState<IProvider | null>(transferPrescription?.provider || null);
    const [isFormValid, setIsFormValid] = useState(true);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);

    const onAddMedication = async () => {
        if (!selectedPharmacy || !selectedProvider) {
            setIsFormSubmitted(true);
            return setIsFormValid(false);
        }

        if (transferPrescription) await dispatch(CustomersController.removeTransferPrescription(transferPrescription));

        const data = {
            prescribable: medication,
            provider: omit(selectedProvider, ['display']),
            fromPharmacy: omit(selectedPharmacy, ['display']),
            toPharmacyNpi: import.meta.env.REACT_APP_PHARMACY_NPI || ''
        } as ITransferPrescriptions;
        try {
            const result = await dispatch(CustomersController.addTransferPrescription(data));
            // add the quantity and days supply separately from the test clim ...
            onCreateMedication?.(result.meta.requestStatus === 'fulfilled' ? result.payload as ITransferPrescriptions : null);
        } catch (error) {
            toast.error('Failed to add medication');
        }
    };

    useEffect(() => {
        if (isFormSubmitted) setIsFormValid(!!selectedPharmacy && !!selectedProvider);
    }, [isFormSubmitted, selectedPharmacy, selectedProvider]);

    return (
        <div className="prescription-medication-form" data-testid="prescription-medication-form">
            <span className="font-agrandir font-medium text-22 md:text-28 lg:text-[1.75rem] mb-[15px] md:mb-[20px] block">{medication.name}</span>

            <Provider
                isEditMode={!!transferPrescription}
                error={!selectedProvider && !isFormValid}
                selectedProvider={selectedProvider}
                setSelectedProvider={setSelectedProvider}
                zipCode={zipCode}
            />
            <Pharmacy
                error={!selectedPharmacy && !isFormValid}
                isEditMode={!!transferPrescription}
                selectedPharmacy={selectedPharmacy}
                zipCode={zipCode}
                setSelectedPharmacy={setSelectedPharmacy}
            />
            {!isFormValid && <Alert testId="prescription-medication-form-error-message" label="Please complete all fields to proceed." className="mt-3" />}
            <Button
                variant="contained"
                disabled={!isFormValid}
                className="prescriptions__form_save_btn mt-[25px] w-full"
                onClick={onAddMedication}
                testId="button-medication-prescrition-save"
                showEndIcon
            >
                Save & Add
            </Button>
        </div>
    );
};

export default withScrollRestoration(PrescriptionMedicationForm);
